/**
 * Entity Configuration
 *
 * Centralized configuration for all platform entities including
 * display properties, icons, colors, and labels.
 *
 * Use this for consistent entity representation across the application:
 * - Navigation
 * - Page headers
 * - Breadcrumbs
 * - Empty states
 * - Related entity links
 *
 * @example
 * ```tsx
 * import { ENTITY_CONFIG } from "@texturehq/edges/config/entities";
 *
 * const siteConfig = ENTITY_CONFIG.site;
 * // { icon: "Buildings", label: { singular: "Site", plural: "Sites" }, ... }
 * ```
 */

import type { IconName } from "../components/Icon";

export interface EntityConfig {
  /** Phosphor icon name */
  icon: IconName;
  /** Display labels */
  label: {
    singular: string;
    plural: string;
  };
  /** Optional description */
  description?: string;
}

export const ENTITY_CONFIG = {
  app: {
    icon: "AppWindow",
    label: {
      singular: "App",
      plural: "Apps",
    },
    description: "Workspace and integration apps",
  },
  site: {
    icon: "MapPin",
    label: {
      singular: "Site",
      plural: "Sites",
    },
    description: "Physical locations with energy systems",
  },
  device: {
    icon: "ShareNetwork",
    label: {
      singular: "Device",
      plural: "Devices",
    },
    description: "Connected energy devices",
  },
  customer: {
    icon: "Users",
    label: {
      singular: "Customer",
      plural: "Customers",
    },
    description: "End customers and site owners",
  },
  contact: {
    icon: "Users",
    label: {
      singular: "Contact",
      plural: "Contacts",
    },
    description: "Contact persons",
  },
  program: {
    icon: "ClipboardText",
    label: {
      singular: "Program",
      plural: "Programs",
    },
    description: "Demand response and VPP programs",
  },
  workflow: {
    icon: "FlowArrow",
    label: {
      singular: "Workflow",
      plural: "Workflows",
    },
    description: "Automated workflows and rules",
  },
  collection: {
    icon: "Stack",
    label: {
      singular: "Collection",
      plural: "Collections",
    },
    description: "Grouped entities",
  },
  board: {
    icon: "SquaresFour",
    label: {
      singular: "Board",
      plural: "Boards",
    },
    description: "Dashboard and monitoring boards",
  },
  report: {
    icon: "ChartLine",
    label: {
      singular: "Report",
      plural: "Reports",
    },
    description: "Analytics and reports",
  },
  event: {
    icon: "ClockCounterClockwise",
    label: {
      singular: "Event",
      plural: "Events",
    },
    description: "System events and activity",
  },
  alert: {
    icon: "Warning",
    label: {
      singular: "Alert",
      plural: "Alerts",
    },
    description: "Notifications and alerts",
  },
  command: {
    icon: "Play",
    label: {
      singular: "Command",
      plural: "Commands",
    },
    description: "Device commands and control",
  },
} as const satisfies Record<string, EntityConfig>;

export type EntityType = keyof typeof ENTITY_CONFIG;

/**
 * Get entity configuration by type
 *
 * @param entityType - The entity type key
 * @returns Entity configuration object
 *
 * @example
 * ```tsx
 * const config = getEntityConfig("site");
 * // { icon: "Buildings", label: { singular: "Site", plural: "Sites" }, ... }
 * ```
 */
export function getEntityConfig(entityType: EntityType): EntityConfig {
  return ENTITY_CONFIG[entityType];
}

/**
 * Get entity icon name
 *
 * @param entityType - The entity type key
 * @returns Phosphor icon name
 *
 * @example
 * ```tsx
 * const iconName = getEntityIcon("site"); // "Buildings"
 * ```
 */
export function getEntityIcon(entityType: EntityType): IconName {
  return ENTITY_CONFIG[entityType].icon;
}

/**
 * Get entity label (singular or plural)
 *
 * @param entityType - The entity type key
 * @param plural - Whether to return plural form
 * @returns Entity label string
 *
 * @example
 * ```tsx
 * getEntityLabel("site", false); // "Site"
 * getEntityLabel("site", true);  // "Sites"
 * ```
 */
export function getEntityLabel(entityType: EntityType, plural: boolean = false): string {
  return plural ? ENTITY_CONFIG[entityType].label.plural : ENTITY_CONFIG[entityType].label.singular;
}
